import * as React from 'react';
import { Box, Container, Typography, Button, Grid, Link, useTheme, styled } from '@mui/material';
import ArrowRight from '@mui/icons-material/ArrowRightAlt';
import CheckCircle from '@mui/icons-material/CheckCircleOutline';
import TrendingUp from '@mui/icons-material/TrendingUp';
import ClockIcon from '@mui/icons-material/AccessTime';
import TargetIcon from '@mui/icons-material/TrackChanges';
import Sparkles from '@mui/icons-material/AutoAwesome';
import Odometer from "odometer";

const PatternBackgroundBox = styled(Box)(({ theme }) => ({
    position: 'absolute',
    inset: 0,
    backgroundImage: `url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTItNnYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6Ii8+PC9nPjwvZz48L3N2Zz4=")`,
    opacity: 0.4,
    pointerEvents: 'none',
}));

const FloatingCardContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: -16,
        bottom: -16,
        left: -16,
        right: -16,
        borderRadius: theme.spacing(2),
        background: 'linear-gradient(to right, #64B5F6, #4DD0E1)',
        filter: 'blur(32px)',
        opacity: 0.3,
        zIndex: -1,
    },
}));

export default function Hero({ siteSettings = {} }) {
    const theme = useTheme();
    const odometerRefs = React.useRef([]);

    const s = siteSettings;

    React.useEffect(() => {
        const stats = [
            { value: parseFloat(s.companies_trust_us_count) || 30 },
            { value: parseFloat(s.issues_resolved) || 12.5 },
            { value: parseFloat(s.customer_satisfaction) || 99.5 },
            { value: parseFloat(s.total_savings) || 80 }
        ];

        stats.forEach((stat, index) => {
            const el = odometerRefs.current[index];
            if (!el) return;

            const odometer = new Odometer({
                el,
                value: 0,
                theme: "default",
            });

            let hasRun = false;
            const observer = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting && !hasRun) {
                    odometer.update(stat.value);
                    hasRun = true;
                }
            }, { threshold: 0.8 });

            observer.observe(el);
        });
    }, [s.companies_trust_us_count, s.issues_resolved, s.customer_satisfaction, s.total_savings]);

    return (
        <Box
            component="section"
            sx={{
                position: 'relative',
                overflow: 'hidden',
                background: 'linear-gradient(to bottom right, #0047AB, #1E5BB8, #0066CC)',
                color: 'white',
                pt: 0,
            }}
        >
            <PatternBackgroundBox />

            <Container maxWidth="xl" sx={{ py: { xs: 5, md: 8, lg: 10 }, position: 'relative', zIndex: 10, px: 3 }}>
                <Grid container spacing={8} alignItems="center" direction={{ xs: 'column', lg: 'row' }}>

                    <Grid item xs={12} lg={6}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 4, md: 6 }
                        }}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    backdropFilter: 'blur(4px)',
                                    px: 2.5,
                                    py: 1,
                                    borderRadius: 999,
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    alignSelf: 'flex-start',
                                }}
                            >
                                <Sparkles sx={{ width: 20, height: 20, color: '#FCD34D' }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                                    AI-Powered 8D Methodology
                                </Typography>
                            </Box>

                            <Typography
                                component="h1"
                                sx={{
                                    fontSize: { xs: '3rem', md: '3.75rem' },
                                    fontWeight: 700,
                                    lineHeight: 1.1,
                                }}
                            >
                                <Box component="span" sx={{ display: 'block', color: 'white', mb: 1 }}>
                                    Transform Quality Management
                                </Box>
                                <Box component="span" sx={{
                                    display: 'block',
                                    background: 'linear-gradient(to right, #BFDBFE, #A5F3FC)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    textFillColor: 'transparent',
                                }}>
                                    with NC Pro
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                                    color: '#DBEAFE',
                                    lineHeight: 1.625,
                                }}
                            >
                                Streamline your 8 Disciplines workflow with AI-powered root cause analysis, automated reporting, and instant insights.
                            </Typography>

                            <Box
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(8px)',
                                    borderRadius: '12px',
                                    p: 3,
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                }}
                            >
                                <Grid container spacing={3}>
                                    <Grid item xs={4}>
                                        <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: 'white', mb: 0.5 }}>{s.time_saved || '95'}%</Typography>
                                        <Typography variant="body2" sx={{ color: '#93C5FD' }}>Time Saved</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: 'white', mb: 0.5 }}>{s.cost_reduction || '87'}%</Typography>
                                        <Typography variant="body2" sx={{ color: '#93C5FD' }}>Cost Reduction</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: 'white', mb: 0.5 }}>Instant</Typography>
                                        <Typography variant="body2" sx={{ color: '#93C5FD' }}>Quick Setup</Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, pt: 1 }}>

                                <Button
                                    component={Link}
                                    href="#demo"
                                    variant="contained"
                                    endIcon={<ArrowRight />}
                                    sx={{
                                        backgroundColor: 'white',
                                        color: '#1E5BB8',
                                        px: 4,
                                        py: 2,
                                        borderRadius: '12px',
                                        fontWeight: 500,
                                        fontSize: '1.01rem',
                                        textTransform: 'none',
                                        boxShadow: theme.shadows[10],
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                            backgroundColor: '#EFF6FF',
                                            boxShadow: theme.shadows[15],
                                            transform: 'scale(1.05)',
                                            '& .MuiButton-endIcon': {
                                                transition: 'transform 0.3s',
                                                transform: 'translateX(4px)',
                                            }
                                        },
                                    }}
                                >
                                    Request Free Demo
                                </Button>

                                <Button
                                    component={Link}
                                    href="#roi"
                                    variant="outlined"
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        backdropFilter: 'blur(4px)',
                                        color: 'white',
                                        px: 4,
                                        py: 2,
                                        borderRadius: '12px',
                                        fontWeight: 500,
                                        fontSize: '1.101rem',
                                        textTransform: 'none',
                                        border: '2px solid rgba(255, 255, 255, 0.3)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                            border: '2px solid rgba(255, 255, 255, 0.3)',
                                        },
                                    }}
                                >
                                    Calculate Your ROI
                                </Button>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, pt: 1, flexWrap: 'wrap' }}>
                                {[
                                    'Life Time License',
                                    'Instant Setup',
                                    'Free Training Included'
                                ].map((text) => (
                                    <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle sx={{ width: 20, height: 20, color: '#86EFAC' }} />
                                        <Typography variant="body2" sx={{ color: '#DBEAFE', fontWeight: 500 }}>
                                            {text}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} lg={6}>
                        <FloatingCardContainer>
                            <Box
                                sx={{
                                    position: 'relative',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '16px',
                                    p: 4,
                                    border: '2px solid rgba(255, 255, 255, 0.2)',
                                    boxShadow: theme.shadows[15],
                                    zIndex: 1,
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <Box sx={{
                                        background: 'linear-gradient(to bottom right, rgba(59, 130, 246, 0.3), rgba(6, 182, 212, 0.3))',
                                        borderRadius: '12px',
                                        p: 3,
                                        border: '1px solid rgba(147, 197, 253, 0.3)',
                                    }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#93C5FD', mb: 0.5 }}>Monthly Savings (By our clients)</Typography>
                                                <Typography sx={{ fontSize: '2.25rem', fontWeight: 700, color: 'white' }}>₹ {s.monthly_savings || '35K'}</Typography>
                                            </Box>
                                            <Box sx={{
                                                width: 56,
                                                height: 56,
                                                bgcolor: '#10B981',
                                                borderRadius: 999,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            >
                                                <TrendingUp sx={{ width: 28, height: 28, color: 'white' }} />
                                            </Box>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#A7F3D0', fontWeight: 500 }}>↑ 127% improvement</Typography>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box sx={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                backdropFilter: 'blur(4px)',
                                                borderRadius: '8px',
                                                p: 2.5,
                                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                            }}
                                            >
                                                <ClockIcon sx={{ width: 24, height: 24, color: '#93C5FD', mb: 1.5 }} />
                                                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'white', mb: 0.5 }}>{s.root_cause_analysis || '5 min'}</Typography>
                                                <Typography variant="caption" sx={{ color: '#93C5FD' }}>Root Cause Analysis</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box
                                                sx={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                    backdropFilter: 'blur(4px)',
                                                    borderRadius: '8px',
                                                    p: 2.5,
                                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                                }}
                                            >
                                                <TargetIcon sx={{ width: 24, height: 24, color: '#67E8F9', mb: 1.5 }} />
                                                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'white', mb: 0.5 }}>{s.resolution_rate || '97'}%</Typography>
                                                <Typography variant="caption" sx={{ color: '#93C5FD' }}>Resolution Rate</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>


                                    <Box
                                        sx={{
                                            background: 'linear-gradient(to right, rgba(245, 158, 11, 0.2), rgba(249, 115, 22, 0.2))',
                                            backdropFilter: 'blur(4px)',
                                            borderRadius: '12px',
                                            p: 2.5,
                                            border: '2px solid rgba(251, 191, 36, 0.3)',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    bgcolor: '#FBBF24',
                                                    borderRadius: 999,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Sparkles sx={{ width: 20, height: 20, color: '#f7f8faff' }} />
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontWeight: 700, color: 'white' }}>AI Analysis Active</Typography>
                                                <Typography variant="caption" sx={{ color: '#FDE68A' }}>Processing {s.ai_analysis_data_points || '247'} data points</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ height: 8, bgcolor: 'rgba(255, 255, 255, 0.2)', borderRadius: 999, overflow: 'hidden' }}>
                                            <Box
                                                sx={{
                                                    height: '100%',
                                                    width: '80%',
                                                    background: 'linear-gradient(to right, #FBBF24, #FB923C)',
                                                    borderRadius: 999,
                                                    transition: 'width 0.5s ease-in-out',
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </FloatingCardContainer>
                    </Grid>
                </Grid>
                <Grid container spacing={{ xs: 3, md: 8 }} sx={{ mt: { xs: 8, md: 10 }, textAlign: 'center' }}>
                    <Grid item xs={6} md={3}>
                        <Typography
                            className="odometer"
                            ref={(el) => (odometerRefs.current[0] = el)}
                            sx={{ fontSize: '2.25rem', fontWeight: 700, color: 'white', mb: 1.5 }}
                        >0</Typography>
                        <Typography component="span" sx={{ fontSize: '2.25rem', fontWeight: 700, color: 'white' }}>+</Typography>
                        <Typography variant="body2" sx={{ color: '#93C5FD' }}>Companies Trust Us</Typography>
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <Typography
                            className="odometer"
                            ref={(el) => (odometerRefs.current[1] = el)}
                            sx={{ fontSize: '2.25rem', fontWeight: 700, color: 'white', mb: 1.5 }}
                        >0</Typography>
                        <Typography component="span" sx={{ fontSize: '2.25rem', fontWeight: 700, color: 'white' }}>k+</Typography>
                        <Typography variant="body2" sx={{ color: '#93C5FD' }}>Issues Resolved</Typography>
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <Typography
                            className="odometer"
                            ref={(el) => (odometerRefs.current[2] = el)}
                            sx={{ fontSize: '2.25rem', fontWeight: 700, color: 'white', mb: 1.5 }}
                        >0</Typography>
                        <Typography component="span" sx={{ fontSize: '2.25rem', fontWeight: 700, color: 'white' }}>%</Typography>
                        <Typography variant="body2" sx={{ color: '#93C5FD' }}>Customer Satisfaction</Typography>
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <Typography
                            className="odometer"
                            ref={(el) => (odometerRefs.current[3] = el)}
                            sx={{ fontSize: '2.25rem', fontWeight: 700, color: 'white', mb: 1.5 }}
                        >0</Typography>
                        <Typography component="span" sx={{ fontSize: '2.25rem', fontWeight: 700, color: 'white' }}>%+</Typography>
                        <Typography variant="body2" sx={{ color: '#93C5FD' }}>Total Savings</Typography>
                    </Grid>
                </Grid>
            </Container>

            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 66,
                    background: 'linear-gradient(to top, white, transparent)',
                    zIndex: 5,
                }}
            />
        </Box>
    );
}